import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import OnScoreTipDialog from './OnScoreTipDialog';
import OffScoreTipDialog from './OffScoreTipDialog';
import '../styles/tips.css';

const ManageTips = ({ 
  isOpen, 
  onClose, 
  currentFileName = null,
  osmdInstance = null,
  visibleParts = [],
  onTipsUpdated = null,
  onTipPlacementModeChange = null,
  onLoadFileRequest = null
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [tips, setTips] = useState([]);
  const [filteredTips, setFilteredTips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showOnScoreDialog, setShowOnScoreDialog] = useState(false);
  const [showOffScoreDialog, setShowOffScoreDialog] = useState(false);
  const [editingTip, setEditingTip] = useState(null);
  const [showFileSelector, setShowFileSelector] = useState(false);
  const [availableFiles, setAvailableFiles] = useState([]);
  
  // Filter states
  const [filterType, setFilterType] = useState('all'); // all, on-score, off-score
  const [filterFile, setFilterFile] = useState('all'); // all, current, general, specific
  const [filterTarget, setFilterTarget] = useState('all'); // all, everyone, specific, roles
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    // console.log('ManageTips: isOpen changed to:', isOpen);
    if (isOpen && user?.id) {
      loadTips();
    }
  }, [isOpen, user]);

  const loadTips = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(
        `${API_BASE}/api/tips?operation=getTips&userId=${user.id}`
      );
      
      if (response.ok) {
        const tipsData = await response.json();
        setTips(tipsData);
        setFilteredTips(tipsData);
      } else {
        throw new Error(t('tips.failedToLoad'));
      }
    } catch (err) {
      console.error('Error loading tips:', err);
      setError(t('tips.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  // Apply filters whenever tips or filter criteria change
  useEffect(() => {
    let filtered = [...tips];

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(tip => tip.type === filterType);
    }

    // File filter
    if (filterFile !== 'all') {
      switch (filterFile) {
        case 'current':
          filtered = filtered.filter(tip => tip.musicxml_id === currentFileName);
          break;
        case 'general':
          filtered = filtered.filter(tip => !tip.musicxml_id);
          break;
        case 'specific':
          filtered = filtered.filter(tip => tip.musicxml_id && tip.musicxml_id !== currentFileName);
          break;
      }
    }

    // Target filter
    if (filterTarget !== 'all') {
      switch (filterTarget) {
        case 'everyone':
          filtered = filtered.filter(tip => tip.target_rules?.everyone);
          break;
        case 'specific':
          filtered = filtered.filter(tip => tip.target_user_ids?.length > 0);
          break;
        case 'roles':
          filtered = filtered.filter(tip => tip.target_rules?.roles?.length > 0);
          break;
      }
    }

    // Search filter
    if (searchText.trim()) {
      const search = searchText.toLowerCase().trim();
      filtered = filtered.filter(tip => 
        tip.content.toLowerCase().includes(search) ||
        (tip.musicxml_id && tip.musicxml_id.toLowerCase().includes(search))
      );
    }

    setFilteredTips(filtered);
  }, [tips, filterType, filterFile, filterTarget, searchText, currentFileName]);

  const createTip = async (tipData) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(
        `${API_BASE}/api/tips?operation=createTip`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(tipData)
        }
      );
      
      if (response.ok) {
        const newTip = await response.json();
        setTips(prevTips => [newTip, ...prevTips]);
        // Close the appropriate dialog
        setShowOnScoreDialog(false);
        setShowOffScoreDialog(false);
        // Notify parent component that tips were updated
        if (onTipsUpdated) {
          onTipsUpdated();
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || t('tips.failedToCreate'));
      }
    } catch (err) {
      console.error('Error creating tip:', err);
      throw err;
    }
  };

  const editTip = async (tip) => {
    // Only load XML file for on-score tips that require a specific file
    if (tip.type === 'on-score' && tip.musicxml_id && tip.musicxml_id !== currentFileName && onLoadFileRequest) {
      try {
        setLoading(true);
        setError(null);
        
        // Request to load the associated XML file for on-score tip positioning
        await onLoadFileRequest(tip.musicxml_id);
        
        // File should now be loaded, proceed with editing
        setEditingTip(tip);
        setShowOnScoreDialog(true);
      } catch (error) {
        console.error('Failed to load XML file for tip editing:', error);
        setError(t('tips.failedToLoadFile', { filename: tip.musicxml_id }));
      } finally {
        setLoading(false);
      }
    } else {
      // No file loading needed (off-score tips or file already loaded), proceed directly with editing
      setEditingTip(tip);
      if (tip.type === 'on-score') {
        setShowOnScoreDialog(true);
      } else {
        setShowOffScoreDialog(true);
      }
    }
  };

  const updateTip = async (tipData) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(
        `${API_BASE}/api/tips?operation=updateTip&id=${editingTip.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(tipData)
        }
      );
      
      if (response.ok) {
        const updatedTip = await response.json();
        setTips(prevTips => prevTips.map(tip => 
          tip.id === editingTip.id ? updatedTip : tip
        ));
        // Close the appropriate dialog
        setShowOnScoreDialog(false);
        setShowOffScoreDialog(false);
        setEditingTip(null);
        // Notify parent component that tips were updated
        if (onTipsUpdated) {
          onTipsUpdated();
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || t('tips.failedToUpdate'));
      }
    } catch (err) {
      console.error('Error updating tip:', err);
      throw err;
    }
  };

  const deleteTip = async (tipId) => {
    if (!window.confirm(t('tips.confirmDelete'))) {
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(
        `${API_BASE}/api/tips?operation=deleteTip&id=${tipId}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        setTips(tips.filter(tip => tip.id !== tipId));
        // Notify parent component that tips were updated
        if (onTipsUpdated) {
          onTipsUpdated();
        }
      } else {
        throw new Error(t('tips.failedToDelete'));
      }
    } catch (err) {
      console.error('Error deleting tip:', err);
      setError(t('tips.failedToDelete'));
    }
  };

  // Utility functions for display
  const getFileDisplay = (tip) => {
    if (!tip.musicxml_id) return t('tips.anyFile');
    if (tip.musicxml_id === currentFileName) return t('tips.currentFile');
    return tip.musicxml_id;
  };

  const getTargetDisplay = (tip) => {
    if (tip.target_rules?.everyone) return t('tips.forEveryone');
    if (tip.target_rules?.roles?.length > 0) return `${t('tips.forRoles')}: ${tip.target_rules.roles.join(', ')}`;
    if (tip.target_user_ids?.length > 0) return `${tip.target_user_ids.length} ${t('tips.specificUsers')}`;
    return t('tips.forEveryone');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const resetFilters = () => {
    setFilterType('all');
    setFilterFile('all');
    setFilterTarget('all');
    setSearchText('');
  };

  // Load available files for selection
  const loadAvailableFiles = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/musicxml?operation=list`);
      const data = await response.json();
      setAvailableFiles(data.files || []);
    } catch (error) {
      console.error('Failed to load available files:', error);
      setAvailableFiles([]);
    }
  };

  // Handle "Create On-Score Tip" click
  const handleCreateOnScoreTip = async () => {
    if (!currentFileName && onLoadFileRequest) {
      // No file loaded, show file selector
      await loadAvailableFiles();
      setShowFileSelector(true);
    } else {
      // File already loaded, proceed with tip creation
      setShowOnScoreDialog(true);
    }
  };

  // Handle file selection for on-score tip creation
  const handleFileSelection = async (filename) => {
    // console.log('ManageTips: Starting file selection for:', filename);
    setShowFileSelector(false);
    try {
      setLoading(true);
      setError(null);
      
      // console.log('ManageTips: Loading file...');
      // Load the selected file
      await onLoadFileRequest(filename);
      // console.log('ManageTips: File loaded successfully');
      
      // Add a small delay to ensure the file is fully loaded and UI is stable
      setTimeout(() => {
        // console.log('ManageTips: Opening on-score dialog after file load');
        // Now open the on-score tip dialog
        setShowOnScoreDialog(true);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Failed to load selected file:', error);
      setError(t('tips.failedToLoadFile', { filename }));
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="tip-modal-overlay">
      <div className="manage-tips-dialog" onClick={e => e.stopPropagation()}>
        <div className="manage-tips-header">
          <h2>{t('tips.manageTips')}</h2>
          <div className="manage-tips-header-actions">
            <button 
              className="create-tip-button primary"
              onClick={handleCreateOnScoreTip}
              style={{ marginRight: '8px' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.14 2 5 5.14 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.86-3.14-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              📍 {t('tips.createOnScore')}
            </button>
            <button 
              className="create-tip-button secondary"
              onClick={() => setShowOffScoreDialog(true)}
              style={{ marginRight: '12px' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              💬 {t('tips.createOffScore')}
            </button>
            <button 
              className="manage-tips-close" 
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="manage-tips-filters">
          <div className="filter-group">
            <input
              type="text"
              placeholder={t('tips.searchTips')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-group">
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="filter-select">
              <option value="all">{t('tips.allTypes')}</option>
              <option value="off-score">📝 {t('tips.off-score')}</option>
              <option value="on-score">📍 {t('tips.on-score')}</option>
            </select>
          </div>

          <div className="filter-group">
            <select value={filterFile} onChange={(e) => setFilterFile(e.target.value)} className="filter-select">
              <option value="all">{t('tips.allFiles')}</option>
              <option value="general">🌐 {t('tips.generalTips')}</option>
              {currentFileName && <option value="current">🎼 {t('tips.currentFile')}</option>}
              <option value="specific">📁 {t('tips.otherFiles')}</option>
            </select>
          </div>

          <div className="filter-group">
            <select value={filterTarget} onChange={(e) => setFilterTarget(e.target.value)} className="filter-select">
              <option value="all">{t('tips.allTargets')}</option>
              <option value="everyone">👥 {t('tips.forEveryone')}</option>
              <option value="specific">👤 {t('tips.specificUsers')}</option>
              <option value="roles">🏷️ {t('tips.forRoles')}</option>
            </select>
          </div>

          {(filterType !== 'all' || filterFile !== 'all' || filterTarget !== 'all' || searchText) && (
            <button onClick={resetFilters} className="reset-filters-btn">
              {t('common.clearFilters')}
            </button>
          )}

          <div className="results-count">
            {filteredTips.length} of {tips.length} {t('tips.tips')}
          </div>
        </div>

        <div className="manage-tips-content">
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>{t('common.loading')}</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={loadTips} className="retry-button">
                {t('common.retry')}
              </button>
            </div>
          )}

          {!loading && !error && (
            <>
              {filteredTips.length === 0 ? (
                <div className="empty-state">
                  <p>{tips.length === 0 ? t('tips.noTips') : t('tips.noMatchingTips')}</p>
                </div>
              ) : (
                <div className="tips-table">
                  <div className="tips-table-header">
                    <div className="col-type">{t('tips.type')}</div>
                    <div className="col-content">{t('tips.content')}</div>
                    <div className="col-file">{t('tips.file')}</div>
                    <div className="col-target">{t('tips.target')}</div>
                    <div className="col-date">{t('tips.created')}</div>
                    <div className="col-actions">{t('tips.actions')}</div>
                  </div>
                  
                  <div className="tips-table-body">
                    {filteredTips.map(tip => (
                      <div key={tip.id} className="tips-table-row">
                        <div className="col-type">
                          <span className={`tip-type-badge ${tip.type}`}>
                            {tip.type === 'on-score' ? '📍' : '📝'}
                          </span>
                        </div>
                        <div className="col-content">
                          <span className="tip-content-preview" title={tip.content}>
                            {tip.content.length > 50 ? tip.content.substring(0, 50) + '...' : tip.content}
                          </span>
                        </div>
                        <div className="col-file">
                          <span className="tip-file-badge" title={tip.musicxml_id || t('tips.anyFile')}>
                            {getFileDisplay(tip)}
                          </span>
                        </div>
                        <div className="col-target">
                          <span className="tip-target-badge" title={getTargetDisplay(tip)}>
                            {getTargetDisplay(tip)}
                          </span>
                        </div>
                        <div className="col-date">
                          <span className="tip-date">{formatDate(tip.created_at)}</span>
                        </div>
                        <div className="col-actions">
                          <button 
                            className="action-btn edit-btn"
                            onClick={() => editTip(tip)}
                            title={t('common.edit')}
                          >
                            ✏
                          </button>
                          <button 
                            className="action-btn delete-btn"
                            onClick={() => deleteTip(tip.id)}
                            title={t('common.delete')}
                          >
                            🗑
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      <OnScoreTipDialog
        isOpen={showOnScoreDialog}
        onClose={() => {
          setShowOnScoreDialog(false);
          setEditingTip(null);
        }}
        onCreateTip={editingTip ? updateTip : createTip}
        editingTip={editingTip}
        musicxmlId={currentFileName}
        osmdInstance={osmdInstance}
        visibleParts={visibleParts}
        onTipPlacementModeChange={onTipPlacementModeChange}
      />
      
      <OffScoreTipDialog
        isOpen={showOffScoreDialog}
        onClose={() => {
          setShowOffScoreDialog(false);
          setEditingTip(null);
        }}
        onCreateTip={editingTip ? updateTip : createTip}
        editingTip={editingTip}
        currentFileName={currentFileName}
      />

      {/* File Selector Dialog for On-Score Tips */}
      {showFileSelector && (
        <div className="tip-modal-overlay">
          <div 
            className="file-selector-dialog"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#1f2937',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '70vh',
              overflowY: 'auto'
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px',
              borderBottom: '1px solid #374151',
              paddingBottom: '16px'
            }}>
              <h3 style={{ color: '#f9fafb', margin: 0 }}>
                {t('tips.selectFileForOnScoreTip')}
              </h3>
              <button 
                onClick={() => setShowFileSelector(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  fontSize: '24px',
                  cursor: 'pointer',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ color: '#d1d5db', marginBottom: '16px', fontSize: '14px' }}>
              {t('tips.selectFileMessage')}
            </div>

            {availableFiles.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                color: '#9ca3af', 
                padding: '40px 20px',
                background: '#374151',
                borderRadius: '6px',
                border: '1px dashed #6b7280'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📁</div>
                <p>{t('tips.noFilesAvailable')}</p>
                <p style={{ fontSize: '12px', marginTop: '8px' }}>
                  {t('tips.uploadFilesFirst')}
                </p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '8px' }}>
                {availableFiles.map((file, index) => (
                  <div
                    key={index}
                    onClick={() => handleFileSelection(file.filename)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: '#374151',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      border: '1px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#4b5563';
                      e.target.style.borderColor = '#6b7280';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#374151';
                      e.target.style.borderColor = 'transparent';
                    }}
                  >
                    <div style={{ fontSize: '20px', marginRight: '12px' }}>🎵</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#f9fafb', fontWeight: '500', marginBottom: '4px' }}>
                        {file.filename}
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: '12px' }}>
                        {(file.size / 1024).toFixed(1)} KB • {new Date(file.uploadedAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTips;